import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  usePostBaseManuscriptCreateOri,
  usePostBaseManuscriptsCreateoriLogdownload,
  usePostBaseManuscriptsSearchDownload,
} from '../../../../api/base-manuscripts/base-manuscripts';
import { BaseManuscriptSearchConditions, BaseManuscriptSearchResult, ErrorInfo } from '../../../../api/model';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import PageNavi from '../../../modules/PageNavi';
import SpinnerModal from '../../../modules/SpinnerModal';
import BaseManualImportModal from './BaseManualImportModal';

const N_DATA_PER_PAGE = 50;

interface Props {
  productionId: number;
  sectionId: number | undefined;
  data: AxiosResponse<BaseManuscriptSearchResult, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  dataError: AxiosError<ErrorInfo> | null;
  setConditionsAndSearch: (value: BaseManuscriptSearchConditions) => void;
}

function BaseManual({ productionId, sectionId, data, isLoading, isError, dataError, setConditionsAndSearch }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 原稿編集画面へのリンク
  const [isOri, setIsOri] = useState<boolean>(data?.data.is_ori ?? false);
  const options = useDefaultOptions(language);
  const navigate = useNavigate();

  // #region 調整ファイルダウンロード
  // API
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    error: downloadError,
    isError: isDownloadError,
    isLoading: downloading,
  } = usePostBaseManuscriptsSearchDownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  useEffect(() => {
    setDisplayDownloadError(isDownloadError);
  }, [isDownloadError]);

  // ダウンロード押下時の動き（ベース原稿一覧に渡す）
  const onClickDownload = useCallback(async () => {
    // ダウンロード API へのアクセス
    await downloadAsync(
      { data: { production_id: productionId } },
      {
        // API アクセスが成功したら、ダウンロードを実行
        onSuccess: (response) => executeDownload(response),
      }
    );
  }, [downloadAsync, productionId]);
  // #endregion

  // #region ORIデータ作成
  // ORIデータ作成ボタン制御
  const [isBase, setIsBase] = useState<boolean>(false);
  const [displayOriCreate, setDisplayOriCreate] = useState(false);
  const [displayConfirmCreateOri, setDisplayConfirmCreateOri] = useState(false);
  const [displayOriError, setOriErrorModal] = useState(false); // oriデータ作成時のエラーモーダルを表示する
  const [displayOriSpinner, setOriSpinnerModal] = useState(false); // oriデータ作成時のスピナーモーダルを表示する
  const [displayOriWarning, setDisplayOriWarning] = useState(false); // oriデータ作成時のワーニング

  // ORIデータ作成API
  const {
    mutateAsync: oriAsync,
    error: oriError,
    isError: isOriError,
    isLoading: isOriloading,
  } = usePostBaseManuscriptCreateOri<AxiosError<ErrorInfo>, unknown>(options);

  // ログダウンロードAPI
  const { mutateAsync: logdownloadAsync } =
    usePostBaseManuscriptsCreateoriLogdownload<AxiosError<Blob>>(optionsForBinary);

  // ORIデータ作成エラー表示
  useEffect(() => {
    setOriErrorModal(isOriError);
    setOriSpinnerModal(isOriloading);
  }, [isOriError, isOriloading]);

  // ORIデータ作成
  const createOri = useCallback(async () => {
    // ダウンロード API へのアクセス
    const oriRes = await oriAsync({ productionId: Number(productionId) });
    if (oriRes.status === 200) {
      if (oriRes.data.error_log_path == null) {
        // 原稿編集へリダイレクト
        navigate(`../../sections/${productionId}/ps?section_id=${Number(oriRes.data.first_section_id)}`); // 画面遷移
      } else {
        // ログ出力apiからログファイルをダウンロード
        setDisplayOriWarning(true);
        await logdownloadAsync(
          { data: { production_id: productionId, error_log_path: oriRes.data.error_log_path } },
          {
            onSuccess: (response) => executeDownload(response),
          }
        );
      }
    }
  }, [oriAsync, productionId, navigate, logdownloadAsync]);

  const onClickOri = useCallback(() => {
    if (isOri) {
      setDisplayConfirmCreateOri(true);
      return;
    }
    void createOri();
  }, [setDisplayConfirmCreateOri, isOri, createOri]);

  // #endregion

  // #region 一覧表示

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('BaseManuscript.CurrentPage', 1));

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // インポートダイアログ表示
  const [showImportModal, setShowImportModal] = useState(false);
  const openImportModal = useCallback(() => {
    setShowImportModal(true);
  }, []);

  // 現在のページに表示する一覧
  const baseData = useMemo(
    () => (data?.data.base_manuscripts ?? []).slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, data]
  );

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('BaseManuscript.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.base_manuscripts != null) {
      setIsOri(Boolean(data?.data.is_ori));
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
      if (data.data.base_manuscripts.length > 0) {
        setIsBase(true);
      }
    }
  }, [data?.data.base_manuscripts, data?.data.is_ori]);

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={dataError} />;
  }

  // データが空の場合
  if (data == null || data.data.base_manuscripts == null || data.data.base_manuscripts.length === 0) {
    /* return <NoDataComponent />; */
  }
  // #endregion

  return (
    <div className="wfr in-pgnavi">
      <div className="master-navi navisep">
        <ul>
          <li>
            <button type="button" className="text-button" onClick={() => openImportModal()}>
              <span>
                <i className="fa-solid fa-upload" />
                {t('調整ファイル読込')}
              </span>
            </button>
          </li>
          <li>
            <Link
              to={`/${language}/productions/sections/${productionId}/ps?section_id=${Number(sectionId)}`}
              target="_blank"
            >
              <span className={isOri ? 'span-fa-enable' : 'span-fa-disable'}>
                <i className="fa-solid fa-pen-to-square" />
                {t('原稿編集画面へ移動')}
              </span>
            </Link>
          </li>
          {isBase && (
            <li>
              <button type="button" className="text-button" onClick={() => setDisplayOriCreate(true)}>
                <span>
                  <i className="fa-solid fa-pen-to-square" />
                  {t('原稿データ作成')}
                </span>
              </button>
            </li>
          )}
        </ul>
        <ul>
          <li>
            <button type="button" className="text-button" onClick={onClickDownload}>
              <span>
                <i className="fa-solid fa-download" />
                {t('調整ファイル出力')}
              </span>
            </button>
          </li>
        </ul>
      </div>
      <div className="scroll">
        <table className="table25">
          <thead>
            <tr>
              <th>
                {t('判定')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('PS No.')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('派生No')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L1')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L2')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L3')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L4')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('小見出し')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('記載内容')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('記載項目')}
                <i className="fa-solid" />
              </th>
            </tr>
          </thead>
          <tbody>
            {/* データ本体 */}
            {baseData.map((x) => (
              <tr key={x.base_manuscript_id}>
                <td>{x.judgment}</td>
                <td>
                  <Link
                    to={`/${language}/ps/view/${Number(x.ps_version_id)}`}
                    target="_blank"
                    style={{ color: 'blue' }}
                  >
                    {x.ps_no}
                  </Link>
                </td>
                <td>{x.derivation_no}</td>
                <td>{x.l1}</td>
                <td>{x.l2}</td>
                <td>{x.l3}</td>
                <td>{x.l4}</td>
                <td>{x.subheading}</td>
                <td>
                  <div style={{ width: '500px', whiteSpace: 'normal' }}>
                    <p>{x.description}</p>
                  </div>
                </td>
                <td>{x.description_item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ページング部分 */}
      {data != null && data.data.base_manuscripts != null && data.data.base_manuscripts.length > 0 && (
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(data.data.base_manuscripts.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      )}
      {/* インポート用モーダル */}
      <BaseManualImportModal
        active={showImportModal}
        productionId={productionId}
        onClose={() => setShowImportModal(false)}
        setConditionsAndSearch={setConditionsAndSearch}
      />

      {/* ORIデータ作成前の確認 */}
      {displayOriCreate && (
        <ConfirmModal
          onClickCancel={() => setDisplayOriCreate(false)}
          onClickOk={() => {
            setDisplayOriCreate(false);
            void onClickOri();
          }}
        >
          {t('原稿データを作成しますか？')}
        </ConfirmModal>
      )}
      {displayConfirmCreateOri && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmCreateOri(false)}
          onClickOk={() => {
            setDisplayConfirmCreateOri(false);
            void createOri();
          }}
        >
          {t('既存のデータがすべて上書きされ、手で修正した内容もクリアされますがよろしいですか？')}
        </ConfirmModal>
      )}
      {/* ORIデータ作成時スピナーモーダルの表示 */}
      {displayOriSpinner && <SpinnerModal />}
      {/* ORIデータ作成時エラーの表示 */}
      {displayOriError && (
        <ErrorModal
          error={oriError}
          onClickOk={() => {
            setOriSpinnerModal(false);
            setOriErrorModal(false);
          }}
        />
      )}

      {/* 調整ファイルダウンロード時スピナーモーダルの表示 */}
      {downloading && <SpinnerModal />}
      {/* 調整ファイルダウンロードエラーの表示 */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
      {displayOriWarning && (
        <OkModal onClickOk={() => setDisplayOriWarning(false)}>
          {t('原稿データでXML構造エラーがあります。ログを確認ください。')}
        </OkModal>
      )}
    </div>
  );
}

export { BaseManual };
